import * as THREE from 'three'
import { EffectComposer }  from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass }      from 'three/addons/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'
import BasePreset from '../base/BasePreset.js'

// Simplex noise GLSL — shared by core + vein shaders
const SNOISE = `
    vec3 mod289(vec3 x){return x-floor(x*(1./289.))*289.;}
    vec4 mod289(vec4 x){return x-floor(x*(1./289.))*289.;}
    vec4 permute(vec4 x){return mod289(((x*34.)+1.)*x);}
    vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
    float snoise(vec3 v){
        const vec2 C=vec2(1./6.,1./3.);const vec4 D=vec4(0.,.5,1.,2.);
        vec3 i=floor(v+dot(v,C.yyy));vec3 x0=v-i+dot(i,C.xxx);
        vec3 g=step(x0.yzx,x0.xyz);vec3 l=1.-g;
        vec3 i1=min(g.xyz,l.zxy);vec3 i2=max(g.xyz,l.zxy);
        vec3 x1=x0-i1+C.xxx;vec3 x2=x0-i2+C.yyy;vec3 x3=x0-D.yyy;
        i=mod289(i);
        vec4 p=permute(permute(permute(
            i.z+vec4(0.,i1.z,i2.z,1.))+i.y+vec4(0.,i1.y,i2.y,1.))+i.x+vec4(0.,i1.x,i2.x,1.));
        float n_=.142857142857;vec3 ns=n_*D.wyz-D.xzx;
        vec4 j=p-49.*floor(p*ns.z*ns.z);vec4 x_=floor(j*ns.z);vec4 y_=floor(j-7.*x_);
        vec4 xg=x_*ns.x+ns.yyyy;vec4 yg=y_*ns.x+ns.yyyy;vec4 h=1.-abs(xg)-abs(yg);
        vec4 b0=vec4(xg.xy,yg.xy);vec4 b1=vec4(xg.zw,yg.zw);
        vec4 s0=floor(b0)*2.+1.;vec4 s1=floor(b1)*2.+1.;vec4 sh=-step(h,vec4(0.));
        vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
        vec3 p0=vec3(a0.xy,h.x);vec3 p1=vec3(a0.zw,h.y);
        vec3 p2=vec3(a1.xy,h.z);vec3 p3=vec3(a1.zw,h.w);
        vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
        p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
        vec4 m=max(.5-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.);m=m*m;
        return 42.*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
    }
`

const CORE_RADIUS  = 2.2
const OUTER_RADIUS = 10.0
const NUM_VEINS    = 1000
const PTS_PER_VEIN = 45

function randOnSphere(r) {
    const u = Math.random(), v = Math.random()
    const theta = 2 * Math.PI * u, phi = Math.acos(2 * v - 1)
    return new THREE.Vector3(
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.sin(phi) * Math.sin(theta),
        r * Math.cos(phi)
    )
}

export default class VoidPreset extends BasePreset {
    constructor() {
        super()
        this.id   = 'void'
        this.name = 'Void'
        this.plan = 'free'
        this.defaultObjectColor = '#00d2ff'
        this.defaultBgColor     = '#000000'

        this._renderer  = null
        this._composer  = null
        this._bloomPass = null
        this._scene     = null
        this._camera    = null
        this._group     = null
        this._sharedU   = null
        this._volMats   = []
        this._dustMesh  = null
        this._toDispose = []
        this._lastW = 0
        this._lastH = 0
        this._smoothedBass = 0
    }

    // ── Public API ─────────────────────────────────────────────────────────────

    // Based on: https://codepen.io/VoXelo/pen/RNGRQBo
    // by VoXelo, MIT License

    getSchema(plan) {
        if (plan === 'free') return []
        const fields = [
            { key: 'objectColor', type: 'color', label: 'Glow Color', default: this.defaultObjectColor },
        ]
        if (plan === 'pro')
            fields.push({ key: 'blobCount', type: 'range', label: 'Vein Density', min: 1, max: 9, step: 1, default: 1 })
        return fields
    }

    init(canvas) {
        this.destroy()

        const [or, og, ob] = this._hexToRgb(this._config.objectColor ?? this.defaultObjectColor)
        const [br, bg, bb] = this._hexToRgb(this.defaultBgColor)
        const bgCol  = new THREE.Color(br, bg, bb)
        const objCol = new THREE.Color(or, og, ob)

        // Renderer — Three.js manages its own WebGL context on the provided canvas
        const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'high-performance', preserveDrawingBuffer: true })
        renderer.setPixelRatio(1)
        renderer.setSize(canvas.width, canvas.height, false)
        renderer.setClearColor(bgCol)
        this._renderer = renderer
        this._lastW = canvas.width
        this._lastH = canvas.height

        const scene = new THREE.Scene()
        scene.fog = new THREE.FogExp2(bgCol.getHex(), 0.012)
        this._scene = scene

        const camera = new THREE.PerspectiveCamera(45, canvas.width / canvas.height, 0.1, 1000)
        camera.position.set(0, 0, 25)
        camera.lookAt(0, 0, 0)
        this._camera = camera

        const bloomPass = new UnrealBloomPass(new THREE.Vector2(canvas.width, canvas.height), 2.0, 0.5, 0.85)
        const composer  = new EffectComposer(renderer)
        composer.addPass(new RenderPass(scene, camera))
        composer.addPass(bloomPass)
        this._composer  = composer
        this._bloomPass = bloomPass

        // Shared uniforms — time + audio reactivity + lava core palette + object-color-driven vein palette
        this._sharedU = {
            time:          { value: 0 },
            uBass:         { value: 0 },
            uMid:          { value: 0 },
            uTreble:       { value: 0 },
            cDark:         { value: new THREE.Color(br * 0.5, bg * 0.5, bb * 0.5) },
            cRed:          { value: new THREE.Color(0.9, 0.05, 0.0) },
            cOrange:       { value: new THREE.Color(1.0, 0.40, 0.0) },
            cYellow:       { value: new THREE.Color(1.0, 0.90, 0.2) },
            cSurface:      { value: objCol.clone() },
            cCoreA:        { value: new THREE.Color(0.8, 0.1, 0.0) },
            cCoreB:        { value: new THREE.Color(1.0, 0.6, 0.0) },
            boundaryColor: { value: new THREE.Color(or * 1.5, og * 1.5, ob * 1.5) },
        }

        const group = new THREE.Group()
        scene.add(group)
        this._group = group

        this._buildDust()
        this._buildCore(group)
        this._buildVeins(group)
        this._buildVolcano(group)
    }

    tick({ time, audioLevel = 0, bass = 0, mid = 0, treble = 0, hover = 0 }) {
        const r = this._renderer
        if (!r) return

        const canvas = r.domElement
        const w = canvas.width, h = canvas.height

        if (w !== this._lastW || h !== this._lastH) {
            this._lastW = w; this._lastH = h
            r.setSize(w, h, false)
            this._composer.setSize(w, h)
            this._camera.aspect = w / h
            this._camera.updateProjectionMatrix()
        }

        this._smoothedBass += (bass - this._smoothedBass) * 0.1
        if (this._sharedU) {
            this._sharedU.time.value    = time
            this._sharedU.uBass.value   = this._smoothedBass
            this._sharedU.uMid.value    = mid
            this._sharedU.uTreble.value = treble
        }
        if (this._group)     this._group.rotation.y = time * 0.05
        if (this._bloomPass) this._bloomPass.strength = 1.2 + this._smoothedBass * 0.8
        if (this._dustMesh)  this._dustMesh.rotation.y += 0.0002

        this._applyConfigUniforms()
        // EffectComposer sets autoClear=false internally — explicitly clear the canvas
        // before composing so preserveDrawingBuffer doesn't accumulate ghost frames
        this._renderer.setRenderTarget(null)
        this._renderer.clear()
        this._composer.render()
    }

    destroy() {
        for (const item of this._toDispose) {
            item.geometry?.dispose()
            item.material?.dispose()
        }
        this._toDispose = []
        this._renderer?.dispose()
        this._renderer  = null
        this._composer  = null
        this._bloomPass = null
        this._scene     = null
        this._camera    = null
        this._group     = null
        this._sharedU   = null
        this._volMats   = []
        this._dustMesh  = null
        this._smoothedBass = 0
        this.gl         = null  // keep BasePreset tick guard from crashing
    }

    // ── Private ────────────────────────────────────────────────────────────────

    _applyConfigUniforms() {
        if (!this._sharedU) return
        const [or, og, ob] = this._hexToRgb(this._config.objectColor ?? this.defaultObjectColor)
        this._sharedU.cSurface.value.setRGB(or, og, ob)
        this._sharedU.boundaryColor.value.setRGB(or * 1.5, og * 1.5, ob * 1.5)
        for (const mat of this._volMats) mat.uniforms.color.value.setRGB(or, og, ob)
    }

    _buildDust() {
        const pos = []
        for (let i = 0; i < 1500; i++)
            pos.push((Math.random()-0.5)*100, (Math.random()-0.5)*100, (Math.random()-0.5)*100)
        const geo = new THREE.BufferGeometry()
        geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
        const mat = new THREE.PointsMaterial({ color: 0x060912, size: 0.08, transparent: true, opacity: 0.10, blending: THREE.AdditiveBlending })
        const mesh = new THREE.Points(geo, mat)
        this._scene.add(mesh)
        this._dustMesh = mesh
        this._toDispose.push({ geometry: geo, material: mat })
    }

    _buildCore(group) {
        const geo = new THREE.SphereGeometry(CORE_RADIUS, 96, 96)
        const mat = new THREE.ShaderMaterial({
            uniforms: this._sharedU,
            vertexShader: `
                uniform float time;
                uniform float uBass;
                varying vec3 vPosition;
                varying vec3 vNormal;
                ${SNOISE}
                void main() {
                    vPosition = position;
                    vNormal   = normal;
                    float d   = snoise(position * 1.8 + time * 0.05) * (0.05 + uBass * 0.18);
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position + normal * d, 1.0);
                }
            `,
            fragmentShader: `
                uniform float time;
                uniform float uBass;
                uniform vec3 cDark;
                uniform vec3 cRed;
                uniform vec3 cOrange;
                uniform vec3 cYellow;
                varying vec3 vPosition;
                varying vec3 vNormal;
                ${SNOISE}
                void main() {
                    float n1 = snoise(vPosition * 1.5 - time * 0.07);
                    float n2 = snoise(vPosition * 4.0 + time * 0.04);
                    float nv = n1 * 0.6 + n2 * 0.4;
                    vec3 col;
                    if      (nv < -0.1) col = mix(cDark,   cRed,    smoothstep(-0.5, -0.1, nv));
                    else if (nv <  0.3) col = mix(cRed,    cOrange, smoothstep(-0.1,  0.3, nv));
                    else                col = mix(cOrange,  cYellow, smoothstep( 0.3,  0.8, nv));
                    float fr = clamp(1.0 - dot(vNormal, vec3(0,0,1)), 0.0, 1.0);
                    col += cOrange * pow(fr, 2.0) * (0.4 + uBass * 0.5);
                    gl_FragColor = vec4(col * (1.1 + uBass * 0.7), 1.0);
                }
            `
        })
        group.add(new THREE.Mesh(geo, mat))
        this._toDispose.push({ geometry: geo, material: mat })
    }

    _buildVeins(group) {
        const positions = [], progress = [], offsets = [], randoms = []

        for (let i = 0; i < NUM_VEINS; i++) {
            const start = randOnSphere(OUTER_RADIUS)
            const end   = start.clone().normalize().multiplyScalar(CORE_RADIUS * 0.85)
            const mid   = new THREE.Vector3().lerpVectors(start, end, 0.5)
            mid.normalize().multiplyScalar(OUTER_RADIUS * 0.55)
            const tan   = new THREE.Vector3().crossVectors(start, new THREE.Vector3(0, 1, 0)).normalize()
            const bitan = new THREE.Vector3().crossVectors(start, tan).normalize()
            mid.add(tan.clone().multiplyScalar((Math.random()-0.5)*6))
            mid.add(bitan.clone().multiplyScalar((Math.random()-0.5)*6))

            const pts = new THREE.QuadraticBezierCurve3(start, mid, end).getPoints(PTS_PER_VEIN)
            const off = Math.random(), rnd = Math.random()

            for (let j = 0; j < PTS_PER_VEIN; j++) {
                positions.push(pts[j].x,   pts[j].y,   pts[j].z)
                positions.push(pts[j+1].x, pts[j+1].y, pts[j+1].z)
                progress.push(j / PTS_PER_VEIN, (j+1) / PTS_PER_VEIN)
                offsets.push(off, off)
                randoms.push(rnd, rnd)
            }
        }

        const geo = new THREE.BufferGeometry()
        geo.setAttribute('position',   new THREE.Float32BufferAttribute(positions, 3))
        geo.setAttribute('progress',   new THREE.Float32BufferAttribute(progress,  1))
        geo.setAttribute('offset',     new THREE.Float32BufferAttribute(offsets,   1))
        geo.setAttribute('randomSeed', new THREE.Float32BufferAttribute(randoms,   1))

        const mat = new THREE.ShaderMaterial({
            uniforms: this._sharedU,
            vertexShader: `
                uniform float time;
                uniform float uBass;
                attribute float progress;
                attribute float offset;
                attribute float randomSeed;
                varying float vProgress;
                varying float vOffset;
                varying float vRandom;
                void main() {
                    vProgress = progress;
                    vOffset   = offset;
                    vRandom   = randomSeed;

                    vec3 pos = position;
                    vec3 radial = normalize(pos);
                    vec3 up = abs(radial.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
                    vec3 perp = normalize(cross(radial, up));

                    float freq = 5.0 + randomSeed * 5.0;
                    float phase = time * freq + randomSeed * 6.2832;
                    float wave = sin(phase + progress * 4.0);
                    pos += perp * wave * (uBass * 0.3);

                    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
                }
            `,
            fragmentShader: `
                uniform float time;
                uniform float uBass;
                uniform float uMid;
                uniform vec3  cSurface;
                uniform vec3  cCoreA;
                uniform vec3  cCoreB;
                varying float vProgress;
                varying float vOffset;
                varying float vRandom;
                void main() {
                    vec3 tgt = mix(cCoreA, cCoreB, vRandom);
                    vec3 col = mix(cSurface, tgt, pow(vProgress, 1.5));

                    float flow = fract(vProgress - time * 0.15 + vOffset);

                    // кожен промінь дихає у своєму ритмі — фаза унікальна через vRandom
                    float breathe = 0.5 + 0.5 * sin(time * 0.18 + vRandom * 6.2832);
                    float pulseWidth = 0.12 + breathe * 0.38;
                    float wave = smoothstep(pulseWidth, 0.0, abs(flow - 0.5));

                    // аудіо: кожен промінь має свою "чутливість" до басу
                    float bassAffinity = 0.3 + 0.7 * vRandom;
                    float intensity = 1.0 + uBass * 1.8 * bassAffinity;
                    col += col * wave * intensity;

                    float a = 0.04 + wave * (0.35 + uBass * 0.15 * bassAffinity);
                    a *= smoothstep(0.0, 0.1, vProgress) * smoothstep(1.0, 0.7, vProgress);

                    gl_FragColor = vec4(col, a);
                }
            `,
            transparent: true,
            blending:    THREE.AdditiveBlending,
            depthWrite:  false
        })

        group.add(new THREE.LineSegments(geo, mat))
        this._toDispose.push({ geometry: geo, material: mat })
    }

    _buildVolcano(group) {
        const loader = new THREE.TextureLoader()
        const textures = [
            loader.load('/particles/1.png'),
            loader.load('/particles/2.png'),
            loader.load('/particles/3.png'),
        ]

        for (let t = 0; t < 3; t++) {
            const pts = []
            for (let i = 0; i < 40; i++) pts.push(randOnSphere(OUTER_RADIUS))
            const geo = new THREE.BufferGeometry().setFromPoints(pts)
            const mat = new THREE.ShaderMaterial({
                uniforms: {
                    color:    { value: this._sharedU.cSurface.value.clone() },
                    size:     { value: 28.0 },
                    time:     this._sharedU.time,
                    uBass:    this._sharedU.uBass,
                    uTreble:  this._sharedU.uTreble,
                    uTexture: { value: textures[t] },
                },
                vertexShader: `
                    uniform float size;
                    uniform float uBass;
                    void main() {
                        vec4 mv = modelViewMatrix * vec4(position, 1.0);
                        gl_PointSize = (size + uBass * 10.0) * (20.0 / -mv.z);
                        gl_Position  = projectionMatrix * mv;
                    }
                `,
                fragmentShader: `
                    uniform sampler2D uTexture;
                    uniform vec3  color;
                    uniform float time;
                    uniform float uBass;
                    uniform float uTreble;
                    void main() {
                        vec4 tex = texture2D(uTexture, gl_PointCoord);
                        float lum = dot(tex.rgb, vec3(0.299, 0.587, 0.114));
                        if (lum < 0.02) discard;
                        float throb = sin(time * (3.0 + uTreble * 5.0) + gl_FragCoord.x) * 0.5 + 0.5;
                        gl_FragColor = vec4(color * tex.rgb * (1.5 + throb + uBass * 2.0), lum);
                    }
                `,
                transparent: true,
                blending:    THREE.AdditiveBlending,
                depthWrite:  false
            })
            this._volMats.push(mat)
            group.add(new THREE.Points(geo, mat))
            this._toDispose.push({ geometry: geo, material: mat })
        }
    }
}
